/**
 * Login and registration.
 *
 * Spec §7: "phone, OTP, done. Under 20 seconds end to end." With a password
 * instead of an OTP the target is easier still — two fields and one button,
 * with no waiting for an SMS that may never arrive on a congested network.
 *
 * `inputMode="numeric"` on both fields matters more than it looks: it brings up
 * the number pad instead of the full keyboard, which on a cheap Android is the
 * difference between a two-tap entry and a fiddly one.
 */

import { useState } from 'react';
import { api, ApiError, storeSession, type Session } from './api.js';
import { S } from './ui.js';
import { formatTzs } from './money.js';

const CAP_OPTIONS = [
  { label: '20,000', senti: '2000000' },
  { label: '50,000', senti: '5000000' },
  { label: '100,000', senti: '10000000' },
  { label: '200,000', senti: '20000000' },
];

export function Login({ onSignedIn }: { onSignedIn: (s: Session) => void }): React.JSX.Element {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [weeklyCapSenti, setWeeklyCapSenti] = useState(CAP_OPTIONS[1]?.senti ?? '5000000');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const session =
        mode === 'login'
          ? await api.login({ phone, password })
          : await api.register({ phone, password, weeklyCapSenti });
      storeSession(session);
      onSignedIn(session);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Imeshindikana kuunganisha. Angalia mtandao wako.',
      );
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = phone.trim().length >= 9 && password.length >= 4;

  return (
    <main style={{ ...S.page, maxWidth: 420, paddingTop: 48 }}>
      <h1 style={{ fontSize: 26, marginBottom: 4 }}>Bongo Jackpot</h1>
      <p style={{ ...S.muted, marginTop: 0, marginBottom: 28 }}>
        Bashiri mechi. Shinda pamoja na wengine.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div style={S.field}>
          <label style={S.label} htmlFor="phone">
            Namba ya simu
          </label>
          <input
            id="phone"
            style={S.input}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="0712 345 678"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div style={S.field}>
          <label style={S.label} htmlFor="password">
            Nenosiri (angalau herufi 4)
          </label>
          <input
            id="password"
            style={S.input}
            type="password"
            inputMode="numeric"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            placeholder="••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {mode === 'register' && (
          <div style={S.field}>
            {/*
              Spec §8: the weekly cap is mandatory at signup and cannot be
              skipped. It is presented as a choice among sensible amounts, not
              as an optional extra a user can scroll past.
            */}
            <label style={S.label}>Kiwango cha juu kwa wiki (lazima)</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {CAP_OPTIONS.map((opt) => (
                <button
                  key={opt.senti}
                  type="button"
                  onClick={() => setWeeklyCapSenti(opt.senti)}
                  aria-pressed={weeklyCapSenti === opt.senti}
                  style={
                    weeklyCapSenti === opt.senti
                      ? {
                          ...S.button,
                          background: 'var(--accent)',
                          color: '#0b1220',
                          borderColor: 'var(--accent)',
                          fontWeight: 700,
                        }
                      : S.button
                  }
                >
                  {opt.label} TZS
                </button>
              ))}
            </div>
            <p style={{ ...S.muted, marginTop: 8 }}>
              Unaweza kupunguza wakati wowote. Kuongeza kunachukua saa 24.
            </p>
          </div>
        )}

        {error !== null && <p style={S.error}>{error}</p>}

        <button
          type="submit"
          style={canSubmit && !busy ? S.buttonPrimary : S.buttonDisabled}
          disabled={!canSubmit || busy}
        >
          {busy ? 'Subiri…' : mode === 'login' ? 'Ingia' : 'Fungua akaunti'}
        </button>
      </form>

      <button
        type="button"
        style={{ ...S.buttonGhost, width: '100%', marginTop: 16 }}
        onClick={() => {
          setMode(mode === 'login' ? 'register' : 'login');
          setError(null);
        }}
      >
        {mode === 'login' ? 'Huna akaunti? Fungua sasa' : 'Una akaunti? Ingia'}
      </button>

      {mode === 'register' && (
        <p style={{ ...S.muted, marginTop: 24, lineHeight: 1.5 }}>
          Kucheza ni kwa wenye miaka 18 na zaidi. Kiwango chako cha wiki ni{' '}
          {formatTzs(weeklyCapSenti)} TZS.
        </p>
      )}
    </main>
  );
}
